import reactUpdate from 'immutability-helper';
import React from 'react';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';

import ScrollableTable, {BaseScrollableTableProps} from './ScrollableTable';
import {
  ASCENDING_SORT,
  CreateCustomSortElement,
  DESCENDING_SORT,
  ScrollableTableColumn,
  ScrollableTableRow,
  SortDirection,
} from './ScrollableTableConstants';

export interface Sort {
  sortKey: string;
  sortDirection: SortDirection;
  customOrderingBehaviorKey?: string;
}

interface TableProps extends Omit<BaseScrollableTableProps, 'rows'> {
  // Optionally apply a first sort to the rows.
  initialRows: Array<ScrollableTableRow>;
  initialSort: Array<{sortKey: string; sortDirection?: SortDirection; customOrderingBehaviorKey?: string}>;
  getCreateCustomSortElementByColumn?: (
    sortList: Array<Sort>,
    // This handler needs to be called in the custom sort handler for the table to re-calculate the row order
    // across all sorts (using compareFunction or getSortValue).
    onSortListChange: (newSortList: Array<Sort>) => void
  ) => {[columnKey: string]: CreateCustomSortElement};
}

interface BaseTableProps extends TableProps {
  compareFunction: (
    rowA: ScrollableTableRow,
    rowB: ScrollableTableRow,
    sortKey: string,
    customOrderingBehaviorKey?: string
  ) => number;
}

interface SimpleTableProps extends TableProps {
  getSortValue: (row: ScrollableTableRow, sortKey: string) => number | string;
}

/**
 * Pass in one of `getSortValue` or `compareFunction` to determine the sort order of the rows.
 *
 * Use `getSortValue` if the row maps cleanly to one value for each column. Use `compareFunction` if you need
 * more fine-grained control over what determines the order.
 */
export type SortableScrollableTableProps = BaseTableProps | SimpleTableProps;

interface State {
  sortList: Array<Sort>;
}

/**
 * A wrapper for ScrollableTable that handles sorting.
 *
 * ScrollableTable expects the parent component to keep track of state (including row order), which
 * makes sense when results are paginated. This wrapper handles keeping track of the row order based on
 * user-specified sorts.
 */
export class BaseSortableScrollableTable extends React.Component<BaseTableProps, State> {
  static defaultProps = {
    initialSort: [],
  };

  override state: State = {
    // eslint-disable-next-line no-restricted-syntax
    sortList: this.props.initialSort.map((sort) => _.extend({sortDirection: ASCENDING_SORT}, sort)),
  };

  override UNSAFE_componentWillReceiveProps(nextProps: BaseTableProps): void {
    // Remove invalid sort keys from sortList.
    const sortList = this.state.sortList.filter(
      // eslint-disable-next-line no-restricted-syntax
      (sort) => _.findIndex(this.props.columns, {sortKey: sort.sortKey}) > -1
    );
    this.setState({sortList: _.compact(sortList)});
  }

  /**
   * Stable sort across several sort keys.
   *
   * Array.prototype.sort is not a stable sort, so this function is a hack to achieve a stable sort
   * for multiple keys. To do this, we iterate through the sortList (i.e. with the primary sort first);
   * for each sort, if the sort values differ, return -1 or 1 based on the comparison; if they
   * are equal, defer to the next sort.
   */
  private stableSortListCompareFunction = (rowA: ScrollableTableRow, rowB: ScrollableTableRow): number => {
    for (const {sortDirection, sortKey, customOrderingBehaviorKey} of this.state.sortList) {
      const result = this.props.compareFunction(rowA, rowB, sortKey, customOrderingBehaviorKey);
      if (result !== 0) {
        const reverseIdentity = sortDirection === DESCENDING_SORT ? -1 : 1;
        return result * reverseIdentity;
      }
    }
    return 0;
  };

  private handleCustomSortUpdate = (updatedSortList: Array<Sort>): void => {
    this.setState({sortList: updatedSortList});
  };

  private handleSort = (sortKey: string): void => {
    // Update the sort list. The sort list contains a list of sorts to be applied to the rows.
    // eslint-disable-next-line no-restricted-syntax
    const sortIdx = _.findIndex(this.state.sortList, {sortKey});
    // The sort direction cycles between ascending and descending, defaulting to ascending sort.
    let newSortDirection = ASCENDING_SORT;
    if (sortIdx > -1 && this.state.sortList[sortIdx].sortDirection === ASCENDING_SORT) {
      newSortDirection = DESCENDING_SORT;
    }
    // If the sort key exists in the current sort list, remove it. Add the new sort to the beginning of the
    // list since it becomes the primary sort.
    const newSortList = this.state.sortList.filter((sort, i) => i !== sortIdx);
    newSortList.unshift({sortKey, sortDirection: newSortDirection});
    this.setState({sortList: newSortList});
  };

  private getColumns(): Array<ScrollableTableColumn> {
    if (this.state.sortList.length === 0) {
      return this.props.columns;
    }
    // Reflect sortDirection accurately for the primary sort. This renders the sort dropdown/dropup to
    // inform the user of the current sort.
    const lastSort = this.state.sortList[0];
    // eslint-disable-next-line no-restricted-syntax
    const colIdx = _.findIndex(this.props.columns, {sortKey: lastSort.sortKey});
    return reactUpdate(this.props.columns, {
      [colIdx]: {$merge: {sortDirection: lastSort.sortDirection}},
    });
  }

  private getSortedRowsAndIndexMapping(): {
    rows: Array<ScrollableTableRow>;
    initialIndexBySortedIndex: Map<number, number>;
    sortedIndexByInitialIndex: Map<number, number>;
  } {
    // Array.prototype.sort is not a stable sort, so return the initial rows if no sort list is specified.
    if (this.state.sortList.length === 0) {
      const identityIndexMap = new Map(this.props.initialRows.map((row, i) => [i, i]));
      return {
        rows: this.props.initialRows,
        initialIndexBySortedIndex: identityIndexMap,
        sortedIndexByInitialIndex: identityIndexMap,
      };
    }
    const rows = this.props.initialRows.slice();
    const initialIndexByRowId = new Map(rows.map((row, i) => [row.id, i]));
    rows.sort(this.stableSortListCompareFunction);
    const initialIndexBySortedIndex = new Map(rows.map((row, i) => [i, initialIndexByRowId.get(row.id)!]));
    const sortedIndexByInitialIndex = new Map(
      Array.from(initialIndexBySortedIndex, ([key, value]) => [value, key])
    );
    return {rows, initialIndexBySortedIndex, sortedIndexByInitialIndex};
  }

  override render(): JSX.Element {
    const {getRowContentHeight, scrollToRow, getCreateCustomSortElementByColumn, ...passThroughProps} =
      this.props;
    const columns = this.getColumns();
    // Some props passed by the parent operate based on the initial row's order. These index mappings are
    // used to translate the indices between the sorted and initial row indices.
    const {rows, initialIndexBySortedIndex, sortedIndexByInitialIndex} = this.getSortedRowsAndIndexMapping();
    return (
      <ScrollableTable
        {...passThroughProps}
        columns={columns}
        getRowContentHeight={
          getRowContentHeight
            ? (tableRowIndex: number) => {
                const index = rows.length > 0 ? initialIndexBySortedIndex.get(tableRowIndex) : tableRowIndex;
                assertAndContinue(index != null, 'Missing initial index');
                return getRowContentHeight(index!);
              }
            : undefined
        }
        onChangeSort={this.handleSort}
        createCustomSortElementByColumn={
          getCreateCustomSortElementByColumn
            ? getCreateCustomSortElementByColumn(this.state.sortList, this.handleCustomSortUpdate)
            : undefined
        }
        scrollToRow={scrollToRow != null ? sortedIndexByInitialIndex.get(scrollToRow) : undefined}
        rows={rows}
      />
    );
  }
}

/**
 * A simple SortableScrollableTable for where every row and sort key map cleanly to one value (no need for
 * tie-breakers).
 */
export function SimpleSortableScrollableTable({
  getSortValue,
  ...passThroughProps
}: SimpleTableProps): JSX.Element {
  const compareFunction = (rowA: ScrollableTableRow, rowB: ScrollableTableRow, sortKey: string): number => {
    let a = getSortValue(rowA, sortKey);
    a = typeof a === 'string' ? a.toUpperCase() : a;
    let b = getSortValue(rowB, sortKey);
    b = typeof b === 'string' ? b.toUpperCase() : b;
    if (a < b) {
      return -1;
    } else if (a > b) {
      return 1;
    } else {
      return 0;
    }
  };

  return <BaseSortableScrollableTable {...passThroughProps} compareFunction={compareFunction} />;
}

function isSimpleTableProps(props: SortableScrollableTableProps): props is SimpleTableProps {
  return (props as SimpleTableProps).getSortValue != null;
}

function SortableScrollableTable(props: SortableScrollableTableProps): JSX.Element {
  if (isSimpleTableProps(props)) {
    return <SimpleSortableScrollableTable {...props} />;
  } else {
    return <BaseSortableScrollableTable {...props} />;
  }
}

export default SortableScrollableTable;
